<template>
  <div
    class="warranty-report-detail"
    @keyup.enter="onFilter"
  >
    <Loader
      v-if="loaderEnabled"
      :logo="loaderLogo"
    ></Loader>
    <KTCodePreview title="Báo cáo bảo hành">
      <template v-slot:toolbar>
        <div class="row">
          <div>
            <b-dropdown
              size="sm"
              id="dropdown-actions"
              right
            >
              <template slot="button-content">
                <i
                  style="font-size: 1rem"
                  class="fas fa-cog"
                ></i>
                <span class="font-weight-bolder">Thao tác</span>
              </template>
              <b-dropdown-item
                v-if="checkPermission('WARRANTY_REPORT_EXPORT')"
                @click="onExportExcel"
              >
                <span>
                  <i
                    style="font-size: 1rem"
                    class="far fa-file-excel"
                  ></i>
                  &nbsp; Xuất Excel
                </span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </template>
      <template v-slot:preview>
        <!-- Bộ lọc -->
        <b-row class="mb-3">
          <b-col cols="2">
            <b-input-group>
              <date-picker
                v-model="filter.dateFrom"
                class="form-control form-control-sm"
                :config="dpConfigs.date"
                placeholder="Từ ngày"
              />
            </b-input-group>
          </b-col>
          <b-col cols="2">
            <b-input-group>
              <date-picker
                v-model="filter.dateTo"
                class="form-control form-control-sm"
                :config="dpConfigs.date"
                placeholder="Đến ngày"
              />
            </b-input-group>
          </b-col>
          <b-col cols="4">
            <BDropdownCustom
              v-model="filter.store"
              :options="optionStores"
              placeholder="Trung tâm"
              text-prop="label"
              value-prop="id"
              :match-key="['label', 'shortName']"
              :searchable="true"
              :limit="10"
            />
          </b-col>
          <b-col cols="2">
            <b-form-select
              v-model="filter.warrantyServiceId"
              size="sm"
              class="form-control"
              :options="serviceTypeOptions"
              value-field="id"
              text-field="name"
            >
              <template #first>
                <b-form-select-option :value="null">Tất cả dịch vụ</b-form-select-option>
              </template>
            </b-form-select>
          </b-col>
          <b-col cols="2">
            <BDropdownCustom
              :value="statusSelectedObjects"
              :options="statusOptions"
              placeholder="Chọn trạng thái"
              :searchable="true"
              :match-key="['name', 'id']"
              text-prop="name"
              value-prop="id"
              @valueChange="onStatusChange"
            />
          </b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col cols="2">
            <b-form-input
              v-model.trim="filter.id"
              size="sm"
              placeholder="ID phiếu"
              @keyup.enter="onFilter"
            />
          </b-col>
          <b-col cols="2">
            <b-form-input
              v-model.trim="filter.code"
              size="sm"
              placeholder="Mã phiếu"
              @keyup.enter="onFilter"
            />
          </b-col>
          <b-col cols="2">
            <EmployeeSearch
              :value="filter.technicianName"
              :employee-id="filter.technicianId"
              placeholder="KTV sửa chữa"
              @employee-selected="onTechnicianSelected"
            />
          </b-col>
          <b-col cols="2">
            <EmployeeSearch
              :value="filter.inspectedByEmployeeName"
              :employee-id="filter.inspectedByEmployeeId"
              placeholder="KTV kiểm tra"
              @employee-selected="onInspectionTechnicianSelected"
            />
          </b-col>
          <b-col cols="2">
            <BDropdownCustom
              :value="criteriaSelectedObjects"
              :options="criteriaOptions"
              placeholder="Đặc điểm"
              :searchable="true"
              :match-key="['name']"
              text-prop="name"
              value-prop="id"
              @valueChange="onCriteriaChange"
            />
          </b-col>
          <b-col cols="2">
            <b-form-input
              v-model="filter.imeiCode"
              size="sm"
              placeholder="Tìm theo IMEI"
              trim
              @keyup.enter="onFilter"
            />
          </b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col cols="4">
            <b-form-input
              v-model="filter.productName"
              size="sm"
              placeholder="Tìm theo tên sản phẩm"
              trim
              @keyup.enter="onFilter"
            />
          </b-col>
          <b-col cols="2">
            <b-form-select
              v-model="filter.companyId"
              size="sm"
              class="form-control"
              :options="listCompany"
              value-field="id"
              text-field="name"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col
            cols="12"
            class="d-flex align-items-center mb-4"
            style="gap: 8px"
          >
            <b-button
              variant="primary"
              size="sm"
              @click="onFilter"
              :disabled="isLoading"
              style="width: 70px"
            >
              Lọc
            </b-button>
            <b-button
              style="font-weight: 600; width: auto; min-width: 120px"
              variant="secondary"
              size="sm"
              @click="onReset"
              :disabled="isLoading || activeFilterCount === 0"
              class="ml-2"
            >
              Xóa bộ lọc
              <span v-if="activeFilterCount > 0">({{ activeFilterCount }})</span>
            </b-button>
          </b-col>
        </b-row>

        <!-- Bảng báo cáo -->
        <div
          v-if="isLoading"
          class="report-table-skeleton"
        >
          <vcl-table
            :speed="5"
            :animate="true"
            :columns="15"
          />
        </div>
        <div
          v-else
          class="table-container"
        >
          <table class="table table-bordered table-sm report-table mb-0">
            <thead>
              <tr class="report-table-group-header">
                <th colspan="1"></th>
                <th
                  colspan="10"
                  class="text-center bg-light"
                >
                  DỊCH VỤ
                </th>
                <th
                  colspan="16"
                  class="text-center bg-light"
                >
                  KỸ THUẬT
                </th>
                <th
                  colspan="10"
                  class="text-center bg-light"
                >
                  HÀNG THAY THẾ
                </th>
              </tr>
              <tr class="report-table-column-header">
                <th>ID</th>
                <th>Mã phiếu</th>
                <!--Dịch vụ -->
                <th>Trung tâm</th>
                <th>Tình trạng</th>
                <th>IMEI</th>
                <th>Loại dịch vụ</th>
                <th>Tên hàng</th>
                <th>Ngày mua</th>
                <th>Tên khách hàng</th>
                <th>SĐT</th>
                <th>Quay lại</th>
                <!--Thông tin kỹ thuật -->
                <th>Ngày tiếp nhận</th>
                <th>Ngày trả dự kiến</th>
                <th>Ngày trả thực tế</th>
                <th>Ghi chú nội bộ</th>
                <th>Mã phiếu sửa ngoài</th>
                <th>Báo giá</th>
                <th class="text-right">Phí hỗ trợ bảo hành</th>
                <th>Tiền phải thu khách</th>
                <th>Tổng xuất linh kiện</th>
                <th>Người lập phiếu</th>
                <th>KTV kiểm tra</th>
                <th>Kỹ thuật viên</th>
                <th class="text-center">Mã phiếu xuất</th>
                <th>Cách sửa</th>
                <th>Không sửa được</th>
                <th>Không đồng ý giá</th>

                <!--Hàng thay thế -->
                <th>KTV xuất linh kiện/dịch vụ</th>
                <th>Lý do xuất linh kiện</th>
                <th>Tên linh kiện/dịch vụ</th>
                <th>Đơn vị tính</th>
                <th>SL</th>
                <th class="text-right">Doanh thu</th>
                <th class="text-right">Chiết khấu</th>
                <th>Giá vốn</th>
                <th>
                  Lãi gộp
                  <i
                    class="fas fa-info-circle text-muted ml-1"
                    v-b-tooltip.hover
                    title="Lãi gộp xuất linh kiện, chưa trừ phí hỗ trợ bảo hành"
                  ></i>
                </th>
                <th>
                  Lãi gộp tổng
                  <i
                    class="fas fa-info-circle text-muted ml-1"
                    v-b-tooltip.hover
                    title="Tổng lãi gộp xuất linh kiện, chưa trừ phí hỗ trợ bảo hành"
                  ></i>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-if="!listItem.length">
                <tr>
                  <td
                    colspan="37"
                    class="text-center text-muted p-4"
                  >
                    Không có dữ liệu
                  </td>
                </tr>
              </template>
              <template v-else>
                <template v-for="(row, rowIndex) in listItem">
                  <!-- Có linh kiện xuất (đã group theo warrantyRepairs.parts): mỗi linh kiện 1 dòng, phiếu chính rowspan theo tổng parts -->
                  <template v-if="row.warrantyRepairs && _getSessionRows(row).length">
                    <tr
                      v-for="(repair, rIdx) in _getSessionRows(row)"
                      :key="`${row.id}-${repair.id || repair.primaryTechnicianId || rIdx}`"
                      :class="[getRowClass(rowIndex), 'tr-' + row.id]"
                      @mouseover="highlightRow(row.id)"
                      @mouseleave="removeHighlightRow(row.id, $event)"
                    >
                      <template v-if="rIdx === 0">
                        <!-- DỊCH VỤ + KỸ THUẬT: rowspan = số phiên sửa có xuất kho -->
                        <td :rowspan="row.sessionRowCount">
                          <span class="hover-link text-primary" @click="onViewWarrantyDetail(row.id)">{{ row.id }}</span></td>
                        <td
                          :rowspan="row.sessionRowCount"
                          class="text-left"
                        >
                          {{ row.code || '' }}
                          <CompanyCodeBadge
                            class="ml-1"
                            :company-code="row.companyCode"
                            :company-name="row.companyName"
                          />
                        </td>
                        <td
                          :rowspan="row.sessionRowCount"
                          class="text-left"
                        >
                          {{ row.storeName }}
                        </td>
                        <td
                          :rowspan="row.sessionRowCount"
                          class="text-center"
                        >
                          <WarrantyStatusBadge
                            :status="row.status"
                            :status-name="row.statusName"
                          />
                        </td>
                        <td
                          :rowspan="row.sessionRowCount"
                          class="text-left"
                        >
                          {{ row.imeiCode }}
                        </td>
                        <td :rowspan="row.sessionRowCount">
                          {{ row.warrantyServiceName || row.servicePackageName }}
                        </td>
                        <td
                          :rowspan="row.sessionRowCount"
                          class="text-left"
                        >
                          {{ row.productName }}
                        </td>
                        <td :rowspan="row.sessionRowCount">
                          {{ row.purchaseDateFormatted }}
                        </td>
                        <td
                          :rowspan="row.sessionRowCount"
                          class="text-left"
                        >
                          {{ row.customerName }}
                        </td>
                        <td :rowspan="row.sessionRowCount">
                          <span
                            v-if="row.customerPhone"
                            class="hover-link text-primary cursor-pointer"
                            @click="linkToCustomer(row)"
                          >{{ row.customerPhone }}</span>
                        </td>
                        <td
                          :rowspan="row.sessionRowCount"
                          class="text-right"
                        >
                          {{ row.returnCount ?? '' }}
                        </td>
                        <td :rowspan="row.sessionRowCount">{{ row.receptionDate }}</td>
                        <td :rowspan="row.sessionRowCount">
                          {{ convertToDateSlash(row.expectedReturnDate, DATE_FORMAT) }}
                        </td>
                        <td :rowspan="row.sessionRowCount">
                          {{ convertToDateSlash(row.actualReturnDate, DATE_FORMAT) }}
                        </td>
                        <td
                          :rowspan="row.sessionRowCount"
                          class="text-left"
                        >
                          {{ row.internalNote }}
                        </td>
                        <td
                          :rowspan="row.sessionRowCount"
                          class="text-left"
                        >
                          <span v-if="row.outsources && row.outsources.length">
                            {{ row.outsources.map((o) => o.code).join(', ') }}
                          </span>
                        </td>
                        <td
                          :rowspan="row.sessionRowCount"
                          class="text-right"
                        >
                          {{ convertPrice(row.estimatedCost) }}
                        </td>
                        <td
                          :rowspan="row.sessionRowCount"
                          class="text-right"
                        >
                          {{ convertPrice(row.supportCost) }}
                        </td>
                        <td
                          :rowspan="row.sessionRowCount"
                          class="text-right"
                        >
                          {{ convertPrice(row.actualCost) }}
                        </td>
                        <td
                          :rowspan="row.sessionRowCount"
                          class="text-right"
                        >
                          {{ _getTotalPartsQuantity(row) }}
                        </td>
                        <td
                          :rowspan="row.sessionRowCount"
                          class="text-left"
                        >
                          {{ row.createdByEmployeeName }}
                        </td>
                        <td
                          :rowspan="row.sessionRowCount"
                          class="text-left"
                        >
                          {{ row.inspectedByEmployeeName }}
                        </td>
                      </template>

                      <!-- Session columns (1 dòng / 1 repair session) -->
                      <td class="text-left">{{ repair.primaryTechnicianName || '' }}</td>
                      <td class="text-left">{{ _aggJoin(repair.parts, 'stockSlipCode') }}</td>
                      <td>{{ repair.repairSolution || repair.solutionName || '' }}</td>
                      <td class="text-center">{{ repair.isCannotRepair ? 'Có' : '' }}</td>
                      <td class="text-center">{{ repair.isDisagreePrice ? 'Có' : '' }}</td>

                      <!-- HÀNG THAY THẾ: aggregate theo session.parts -->
                      <td class="text-left">{{ _aggJoin(repair.parts, 'createdByName') }}</td>
                      <td class="text-left">{{ _aggJoin(repair.parts, 'exportReasonName') }}</td>
                      <td class="text-left">{{ _aggJoin(repair.parts, 'productName') }}</td>
                      <td>{{ _aggJoin(repair.parts, 'unitName') }}</td>
                      <td class="text-right">{{ _aggSum(repair.parts, 'quantity') }}</td>
                      <td class="text-right">
                        {{ convertPrice(_aggSum(repair.parts, 'totalAmount')) }}
                      </td>
                      <td class="text-right">
                        {{ convertPrice(_aggSum(repair.parts, 'discountAmount')) }}
                      </td>
                      <td class="text-right">
                        {{ convertPrice(_aggSum(repair.parts, 'originalPrice')) }}
                      </td>
                      <td class="text-right">
                        {{ convertPrice(Number(repair.totalProfit) || 0) }}
                      </td>
                      <td
                        v-if="rIdx === 0"
                        :rowspan="row.sessionRowCount"
                        class="text-right"
                      >
                        {{ convertPrice(Number(row.totalProfitSum) || 0) }}
                      </td>
                    </tr>
                  </template>
                  <!-- Không có linh kiện xuất: 1 dòng, cột HÀNG THAY THẾ để trống -->
                  <tr
                    v-else
                    :key="row.id"
                    :class="rowIndex % 2 === 0 ? 'report-row-even' : 'report-row-odd'"
                  >
                    <td>
                      <span class="hover-link text-primary" @click="onViewWarrantyDetail(row.id)">{{ row.id }}</span>
                    </td>
                    <td class="text-left">{{ row.code || '' }}                       
                      <CompanyCodeBadge
                        class="ml-1"
                        :company-code="row.companyCode"
                        :company-name="row.companyName"
                      />
                    </td>
                    <td class="text-left">{{ row.storeName }}</td>
                    <td class="text-center">
                      <WarrantyStatusBadge
                        :status="row.status"
                        :status-name="row.statusName"
                      />
                    </td>
                    <td class="text-left">{{ row.imeiCode }}</td>
                    <td>{{ row.warrantyServiceName || row.servicePackageName }}</td>
                    <td class="text-left">{{ row.productName }}</td>
                    <td>{{ row.purchaseDateFormatted }}</td>
                    <td class="text-left">{{ row.customerName }}</td>
                    <td>
                      <span
                        v-if="row.customerPhone"
                        class="hover-link text-primary cursor-pointer"
                        @click="linkToCustomer(row)"
                      >{{ row.customerPhone }}</span>
                    </td>
                    <td class="text-right">{{ row.returnCount ?? '' }}</td>
                    <td>{{ row.receptionDate }}</td>
                    <td>{{ convertToDateSlash(row.expectedReturnDate, DATE_FORMAT) }}</td>
                    <td>{{ convertToDateSlash(row.actualReturnDate, DATE_FORMAT) }}</td>
                    <td class="text-left">{{ truncateText(row.internalNote, 30) }}</td>
                    <td class="text-left">
                      <span v-if="row.outsources && row.outsources.length">
                        {{ row.outsources.map((o) => o.code).join(', ') }}
                      </span>
                    </td>
                    <td class="text-right">{{ convertPrice(row.estimatedCost) }}</td>
                    <td class="text-right">{{ convertPrice(row.supportCost) }}</td>
                    <td class="text-right">{{ convertPrice(row.actualCost) }}</td>
                    <td class="text-right">{{ row.totalPartQuantity || 0 }}</td>
                    <td class="text-left">{{ row.createdByEmployeeName }}</td>
                    <td class="text-left">{{ row.inspectedByEmployeeName }}</td>
                    <td class="text-left">{{ row.primaryTechnicianName }}</td>
                    <td class="text-left">
                      {{
                        row.replaceProducts && row.replaceProductCount
                          ? row.replaceProducts.map((p) => p.stockSlipCode).join(', ')
                          : ''
                      }}
                    </td>
                    <td>{{ row.repairSolution || '' }}</td>
                    <td class="text-center">
                      {{ row.warrantyRepairs?.length && row.warrantyRepairs.every((r) => r.isCannotRepair) ? 'Có' : '' }}
                    </td>
                    <td class="text-center">
                      {{ row.warrantyRepairs?.length && row.warrantyRepairs.every((r) => r.isDisagreePrice) ? 'Có' : '' }}
                    </td>
                    <td
                      colspan="10"
                      class="text-center text-muted"
                    ></td>
                  </tr>
                </template>
              </template>
            </tbody>
          </table>
        </div>
        <b-row class="mt-4">
          <b-col>
            <span class="mt-3 d-inline-block text-dark font-weight-bold mb-0">
              Hiển thị {{ detailFrom }} - {{ detailTo }} / {{ totalRow }} phiếu
            </span>
          </b-col>
          <b-col>
            <BPaginationNavCustom
              v-if="numberOfPage > 1"
              :number-of-pages="numberOfPage"
              :current-page="currentPage"
              :total-page="totalPage"
              @page-change="onPageChange"
            />
          </b-col>
        </b-row>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import { VclTable } from 'vue-content-loading'
import KTCodePreview from '@/view/content/CodePreview.vue'
import Loader from '@/view/content/Loader.vue'
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module'
import { mapGetters } from 'vuex'
import { formatDBDate, convertToDateSlash, DATE_FORMAT } from '@/utils/date'
import { WARRANTY_RECEPTION_STATUS_NAME } from '@/utils/enum-name'
import apiCommonMixin from '@/view/mixins/api-common.mixins'
import {
  getWarrantyReportDetail,
  getWarrantyServices,
  exportWarrantyReportDetailExcel,
} from '../utils/warrantyApi'
import BDropdownCustom from '@/view/base/bootstrap/BDropdownCustom.vue'
import EmployeeSearch from '@/view/components/common/EmployeeSearch.vue'
import WarrantyStatusBadge from '../components/WarrantyStatusBadge.vue'
import CompanyCodeBadge from '@/view/components/common/CompanyCodeBadge.vue'
import { convertPrice, truncateText } from '@/utils/common'
import BPaginationNavCustom from '@/view/base/bootstrap/BPaginationNavCustom.vue'
import { WARRANTY_STATUS_OPTIONS } from '@/utils/constants'
import './WarrantyReportDetailStyles.css'
import permissionMixin from '@/view/mixins/permission.mixin'

export default {
  mixins: [apiCommonMixin, permissionMixin],
  components: {
    KTCodePreview,
    Loader,
    BDropdownCustom,
    EmployeeSearch,
    WarrantyStatusBadge,
    CompanyCodeBadge,
    VclTable,
    BPaginationNavCustom,
  },
  data() {
    return {
      DATE_FORMAT,
      filter: {
        dateFrom: null,
        dateTo: null,
        store: [],
        status: [],
        id: null,
        code: null,
        technicianId: null,
        technicianName: '',
        inspectedByEmployeeId: null,
        inspectedByEmployeeName: '',
        criteriaIds: [],
        productName: '',
        warrantyServiceId: null,
        imeiCode: '',
        companyId: null,
      },
      optionStores: [],
      listCompany: [],
      serviceTypeOptions: [],
      statusOptions: WARRANTY_STATUS_OPTIONS,
      criteriaOptions: [
        { id: 'HAS_DEBT', name: 'Còn công nợ' },
        { id: 'NO_DEBT', name: 'Hết công nợ' },
        { id: 'CANNOT_REPAIR', name: 'Không sửa được' },
        { id: 'DISAGREE_PRICE', name: 'Không đồng ý giá' },
      ],
      listItem: [],
      isLoading: false,
      currentPage: 1,
      pageSize: 20,
      totalRow: 0,
      totalPage: 1,
    }
  },
  computed: {
    numberOfPage() {
      return this.totalPage >= 1 ? this.totalPage : 1
    },
    detailFrom() {
      if (this.totalRow === 0) return 0
      return (this.currentPage - 1) * this.pageSize + 1
    },
    detailTo() {
      return (this.currentPage - 1) * this.pageSize + this.listItem.length
    },
    ...mapGetters(['layoutConfig']),
    loaderEnabled() {
      return !/false/.test(this.layoutConfig('loader.type'))
    },
    loaderLogo() {
      return process.env.BASE_URL + this.layoutConfig('loader.logo')
    },
    activeFilterCount() {
      const f = this.filter
      let count = 0
      if (f.dateFrom) count++
      if (f.dateTo) count++
      if (f.id != null && String(f.id).trim()) count++
      if (f.code != null && String(f.code).trim()) count++
      const store = Array.isArray(f.store) ? f.store : []
      if (store.length > 0) count++
      if (f.status && (!Array.isArray(f.status) || f.status.length > 0)) count++
      if (f.technicianId) count++
      if (f.inspectedByEmployeeId) count++
      if ((Array.isArray(f.criteriaIds) ? f.criteriaIds : []).length > 0) count++
      if (f.productName && f.productName.trim()) count++
      if (f.warrantyServiceId) count++
      if (f.imeiCode && f.imeiCode.trim()) count++
      if (f.companyId) count++
      return count
    },
    criteriaSelectedObjects() {
      const ids = this.filter.criteriaIds || []
      return this.criteriaOptions.filter((c) => ids.includes(c.id))
    },
    statusSelectedObjects() {
      const selected = Array.isArray(this.filter.status) ? this.filter.status : []
      return this.statusOptions.filter((s) => selected.includes(s.id))
    },
    dpConfigs() {
      return {
        date: {
          format: 'DD/MM/YYYY',
          useCurrent: false,
          showClear: true,
          showClose: true,
          locale: 'vi',
          allowInputToggle: true,
        },
      }
    },
  },
  created() {
    this.fetchStores()
    this.fetchServiceTypes()
    this.fetchCompany()
    this.fetchData()
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: 'Báo cáo tổng hợp' }])
  },
  methods: {
    onViewWarrantyDetail(id) {
      const route = this.$router.resolve({ name: 'warranty-dispatch-info', query: { id } })
      window.open(route.href, '_blank')
    },
    linkToCustomer(row) {
      if (!row?.customerId && !row?.customerPhone) return
      const route = this.$router.resolve({
        name: 'update-customer',
        query: { id: row.customerId, phoneNo: row.customerPhone },
      })
      window.open(route.href, '_blank')
    },
    truncateText,
    convertPrice,
    convertToDateSlash,
    mapDataTreeSelect(items = [], keys = ['id', 'name', 'shortName']) {
      if (!items || items.length === 0) return []
      const [idKey, nameKey, shortNameKey] = keys
      return items.map((item) => ({
        id: item[idKey],
        label: item[nameKey] || item[shortNameKey],
        shortName: item[shortNameKey] || item[nameKey],
      }))
    },
    async fetchStores() {
      try {
        const { stores } = await this.fetchStoreByUser()
        this.optionStores = this.mapDataTreeSelect(stores, ['id', 'name', 'shortName'])
      } catch (e) {
        this.optionStores = []
      }
    },
    async fetchServiceTypes() {
      try {
        const list = await getWarrantyServices()
        this.serviceTypeOptions = (list || []).map((s) => ({
          id: s.id,
          name: s.name || s.serviceName,
        }))
      } catch (e) {
        this.serviceTypeOptions = []
      }
    },
    async fetchCompany() {
      const list = await this.fetchCompanies()
      this.listCompany = [{ id: null, name: 'Tất cả doanh nghiệp' }, ...list]
    },
    onTechnicianSelected(employee) {
      this.filter.technicianId = employee ? employee.id : null
      this.filter.technicianName = employee ? employee.name : ''
    },
    mapRow(item) {
      const statusName =
        WARRANTY_RECEPTION_STATUS_NAME[item.status] || item.statusName || item.status
      return {
        ...item,
        statusName,
        receptionDate: convertToDateSlash(item.createdAt, DATE_FORMAT),
        purchaseDateFormatted: item.purchaseDate
          ? convertToDateSlash(item.purchaseDate, DATE_FORMAT)
          : null,
        completedAtFormatted: item.completedAt
          ? convertToDateSlash(item.completedAt, DATE_FORMAT)
          : null,
      }
    },
    getParamFilters() {
      const storeIds = this.filter.store.map((s) => s?.id).filter(Boolean)
      return {
        page: this.currentPage,
        limit: this.pageSize,
        mode: 'dispatch',
        storeIds: storeIds.length ? storeIds : undefined,
        primaryTechnicianId: this.filter.technicianId || undefined,
        inspectedByEmployeeId: this.filter.inspectedByEmployeeId || undefined,
        criteriaIds:
          Array.isArray(this.filter.criteriaIds) && this.filter.criteriaIds.length
            ? this.filter.criteriaIds
            : undefined,
        startDate: formatDBDate(this.filter.dateFrom),
        endDate: formatDBDate(this.filter.dateTo),
        statuses: this.filter.status?.length ? this.filter.status : undefined,
        productName: this.filter.productName?.trim() || undefined,
        warrantyServiceId: this.filter.warrantyServiceId || undefined,
        imeiCode: this.filter.imeiCode?.trim() || undefined,
        companyId: this.filter.companyId || undefined,
        id: this.filter.id != null && String(this.filter.id).trim()
          ? String(this.filter.id).trim()
          : undefined,
        code: this.filter.code != null && String(this.filter.code).trim()
          ? String(this.filter.code).trim()
          : undefined,
      }
    },
    async fetchData() {
      if (this.isLoading) return
      this.isLoading = true
      try {
        const params = this.getParamFilters()
        const data = await getWarrantyReportDetail(params)
        this.listItem = (data.dataset || []).map((item) => this.mapRow(item))
        this.totalRow = data.totalRow ?? 0
        this.totalPage = (data.totalPage ?? Math.ceil((data.totalRow || 0) / this.pageSize)) || 1
      } catch (err) {
        this.listItem = []
      } finally {
        this.isLoading = false
      }
    },
    onFilter() {
      this.currentPage = 1
      this.fetchData()
    },
    async onExportExcel() {
      const params = this.getParamFilters()
      delete params.page
      delete params.limit
      await exportWarrantyReportDetailExcel(params)
    },
    onStatusChange(selectedItems = []) {
      this.filter.status = selectedItems.map((s) => s.id)
    },
    onCriteriaChange(selectedObjects) {
      this.filter.criteriaIds = (selectedObjects || []).map((o) => o.id)
    },
    onInspectionTechnicianSelected(employee) {
      this.filter.inspectedByEmployeeId = employee ? employee.id : null
      this.filter.inspectedByEmployeeName = employee ? employee.name : ''
    },
    onReset() {
      this.filter = {
        dateFrom: null,
        dateTo: null,
        store: [],
        status: [],
        id: null,
        code: null,
        technicianId: null,
        technicianName: '',
        inspectedByEmployeeId: null,
        inspectedByEmployeeName: '',
        criteriaIds: [],
        productName: '',
        warrantyServiceId: null,
        imeiCode: '',
        companyId: null,
      }
      this.currentPage = 1
      this.fetchData()
    },
    onPageChange(pageNum) {
      this.currentPage = pageNum
      this.fetchData()
    },
    getRowClass(rowIndex) {
      return rowIndex % 2 === 0 ? 'report-row-even' : 'report-row-odd'
    },
    _getSessionRows(row) {
      const repairs = Array.isArray(row?.warrantyRepairs) ? row.warrantyRepairs : []
      return repairs.filter((r) => Array.isArray(r?.parts) && r.parts.length > 0)
    },
    _getTotalPartsQuantity(row) {
      const repairs = this._getSessionRows(row)
      return repairs.reduce((sum, r) => sum + this._aggSum(r.parts, 'quantity'), 0)
    },
    _getStockSlipCodes(row) {
      const repairs = this._getSessionRows(row)
      const codes = repairs.flatMap((r) => (Array.isArray(r?.parts) ? r.parts : []))
      return this._aggJoin(codes, 'stockSlipCode')
    },
    _aggSum(list, key) {
      const arr = Array.isArray(list) ? list : []
      return arr.reduce((sum, item) => sum + (Number(item?.[key]) || 0), 0)
    },
    _aggJoin(list, key) {
      const arr = Array.isArray(list) ? list : []
      const uniq = Array.from(new Set(arr.map((item) => item?.[key]).filter(Boolean)))
      return uniq.join(', ')
    },
    removeHighlightRow(id, event) {
      if (!id) return

      const relatedTarget = event?.relatedTarget
      if (relatedTarget && relatedTarget.closest) {
        const nextInGroup = relatedTarget.closest(`.tr-${id}`)
        if (nextInGroup) return
      }

      const rows = document.querySelectorAll(`.tr-${id}`)
      if (rows.length) {
        rows.forEach((item) => {
          item.classList.remove('table-hover')
        })
      }
    },
    highlightRow(id) {
      if (!id) return
      const rows = document.querySelectorAll(`.tr-${id}`)
      if (rows.length) {
        rows.forEach((item) => {
          item.classList.add('table-hover')
        })
      }
    },
  },
}
</script>

<style scoped>
.warranty-report-detail .table-hover {
  background-color: #a8bbbf33 !important;
}

/* Căn giữa toàn bộ nội dung bảng (trừ các cột đã set text-right/text-left/...) */
.warranty-report-detail table th:not(.text-right):not(.text-left):not(.text-start):not(.text-end),
.warranty-report-detail table td:not(.text-right):not(.text-left):not(.text-start):not(.text-end) {
  text-align: center;
  vertical-align: middle;
}
</style>
